import type { NextPage } from "next";
import { createClient } from "../../../prismicio";
import { SliceZone } from "@prismicio/react";
import { PrismicDocumentWithUID } from "@prismicio/types";
import { components } from "../../slices";
import { getSlice } from "../../utils/sliceHelper";
import React from "react";
import LDJson from "../../components/ldjson";
import MetaFields from "../../components/MetaFields";
import { useRouter } from "next/router";
import {
  DEFAULT_CHUNK_SIZE,
  fixCallToActions,
} from "../../utils/callToActionHelper";

interface IPreviewData {
  ref?: string;
}

interface IPageProps {
  params: any;
  locale?: string;
  previewData: IPreviewData;
}

export async function getStaticProps({
  params,
  locale,
  previewData,
}: IPageProps) {
  let page: PrismicDocumentWithUID;
  let uid;
  const client = await createClient({ previewData });
  let mapLocale = "nl-nl";
  /*
   * `params.uid` contains an array of each part of the URL separated by a `/`.
   * In this example, the last part is the document's UID.
   */

  try {
    uid = params.page;
    mapLocale = "nl" === locale ? "nl-nl" : "en-gb";

    page = await client.getByUID("pages", uid, {
      lang: mapLocale,
      fetchLinks: ["parentPage"],
    });

    if (page?.data?.parent?.uid !== undefined) {
      return {
        notFound: true,
      };
    }
  } catch (e: any) {
    return {
      notFound: true,
    };
  }

  // Get the projects from the highlighted projects slices repeatable zone
  await Promise.all(
    page.data.slices.map(async (slice: any) => {
      if (slice.slice_type === "highlighted_projects") {
        const projects = await client.getAllByUIDs(
          "pages",
          slice.items.map((item: any) => item.projectLink.uid),
          {
            lang: mapLocale,
          }
        );
        slice.projects = projects;
      }
      if (slice.slice_type === "highlighted_project") {
        const project = await client.getByUID("pages", slice.primary.link.uid, {
          lang: mapLocale,
        });
        slice.project = project;
      }
    })
  );

  const globalSettings = await client.getSingle("global_settings", {
    lang: mapLocale,
  });

  const jobs = await client.getAllByTag("Vacatures", {
    lang: mapLocale,
  });

  const footer = getSlice(globalSettings.data.slices, "slice_type", "footer");
  const navigation = getSlice(
    globalSettings.data.slices,
    "slice_type",
    "navigation"
  );
  const services = getSlice(
    globalSettings.data.slices,
    "slice_type",
    "services"
  );
  // @ts-ignore
  const footerSections = globalSettings.data.slices.filter((slice: any) => {
    return slice
      ? ["location", "contact", "links", "socials"].includes(slice.slice_type)
      : null;
  });

  return {
    props: {
      page,
      services,
      footer,
      navigation,
      jobsCount: jobs?.length || 9,
      globalSettings,
      footerSections,
      env: {
        SITE_URL: process.env.SITE_URL,
        ROBOTS: process.env.ROBOTS,
      },
    },
  };
}

export async function getStaticPaths() {
  const client = await createClient();
  const pages = await client.getAllByType("pages", {
    lang: "*",
    fetchLinks: ["parent"],
  });

  const paths = pages
    .filter((page) => {
      //@ts-ignore
      return page.data.parent.uid === undefined;
    })
    .filter((page) => {
      return page.uid !== "404";
    })
    .map((page) => {
      return {
        params: { page: page.uid },
        locale: page.lang === "nl-nl" ? "nl" : "en",
      };
    });
  return {
    paths,
    fallback: false,
  };
}

const Page: NextPage = ({
  page,
  jobsCount,
  footer,
  globalSettings,
  footerSections,
  env,
}: any) => {
  const separate = ["header", "breadcrumb"];
  const page_router = useRouter();

  const mainSlices = fixCallToActions(
    page.data.slices.filter(
      (slice: any) => !separate.includes(slice.slice_type)
    ),
    globalSettings,

    // Bigger chunk size for projecten page
    page.uid === "projecten" ? 8 : DEFAULT_CHUNK_SIZE
  );

  return (
    <>
      {/* These will be added to <head> */}
      <MetaFields
        page={page}
        globalSettings={globalSettings}
        pageRouter={page_router}
        env={env}
      />
      <LDJson page={page} globals={globalSettings} />
      {/* </head> */}

      <header className="flex flex-col mb-14 lg:mb-24">
        <SliceZone
          slices={page.data.slices.filter((slice: any) =>
            separate.includes(slice.slice_type)
          )}
          components={{ ...components }}
          context={{ globalSettings, jobsCount }}
        />
      </header>
      <main className="overflow-x-hidden flex flex-col px-8 space-y-14 mb-14 lg:space-y-32 mb-14 lg:mb-32">
        <SliceZone
          slices={mainSlices}
          components={{ ...components }}
          context={{ globalSettings }}
        />
      </main>
      <footer className="bg-brand-marine px-8">
        <SliceZone
          slices={[footer]}
          components={{ ...components }}
          context={{ footer, globalSettings, footerSections }}
        />
      </footer>
    </>
  );
};
export default Page;
